import {
  type ConsentModalDefinition,
  type CustomerModalDefinition,
  type SelectCoworkerAndTeamModalDefinition,
  type SelectDepartmentModalDefinition,
  type hfb,
  isTeamSelection,
  useConsent,
  useTeam,
} from "@repo/api";
import {
  FormField,
  ListView,
  ListViewItem,
  Text,
  briefcaseIcon,
  mapIcon,
  speechBubbleIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { hasItems, isDefined, trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useEffect } from "react";

import { RegionContainer } from "../../components/RegionContainer";
import { SectionContainer } from "../../components/SectionContainer";
import { CoworkerAndTeamSelectorListItem } from "../../components/coworkerSelection/CoworkerAndTeamSelectorListItem";
import { useOrderedDepartments } from "../../hooks/useCMP";
import { useSettings } from "../../settings/SettingsProvider";
import {
  getConsentDetails,
  getConsentValue,
  isConsentValid,
} from "../../utils/consentHelpers";
import {
  translateContactMethod,
  translateHFBName,
} from "../../utils/translationHelpers";
import {
  type NewProjectState,
  type NewProjectStateActions,
} from "./NewProjectReducer";

interface NewProjectStepProps {
  state: NewProjectState;
  actions: NewProjectStateActions;
  pushModal: (
    modal:
      | SelectCoworkerAndTeamModalDefinition
      | SelectDepartmentModalDefinition
      | ConsentModalDefinition
      | CustomerModalDefinition
  ) => void;
}

const getSelectedDepartmentName = (
  departments: [hfb, string][],
  selected: hfb[]
) =>
  selected.map(
    (el) => departments.find((department) => department[0] === el)?.[1] ?? el
  );

export const NewProjectStep = ({
  state,
  actions,
  pushModal,
}: NewProjectStepProps) => {
  const departments = useOrderedDepartments();
  const { consent_requirements } = useSettings();
  const { data: assignedTeamData } = useTeam(state.assignedTeam);
  const teamSelection = assignedTeamData
    ? {
        kind: "team" as const,
        team: assignedTeamData,
        assignedToTeam: true,
        members: (assignedTeamData.teamMembers ?? []).map((m) => ({
          ...m,
          selected: true,
        })),
      }
    : undefined;
  const { data: consentData, isValidating: isValidatingConsent } = useConsent(
    state.customerInfo?.partyId,
    true
  );
  const followupConsent = getConsentValue(
    "FOLLOWUP_CONSENT",
    consentData?.customerConsents,
    state.opportunityInfo.hfb
  );

  const promotionalConsent = consent_requirements?.promotional_consent
    ? getConsentValue(
        "PROMOTIONAL_CONSENT",
        consentData?.customerConsents,
        state.opportunityInfo.hfb
      )
    : undefined;

  const consentDetails = getConsentDetails(
    followupConsent,
    state.manualConsents?.find((el) => el.consentType === "FOLLOWUP_CONSENT")
      ?.consentProvided
  );

  // set consent based on API response
  useEffect(() => {
    if (!isValidatingConsent) {
      actions.setCustomerConsents(
        [followupConsent, promotionalConsent].filter(isDefined)
      );

      actions.setContactMethod(
        state.contactMethod || consentData?.preferredContactType || "EMAIL"
      );
    }
  }, [
    followupConsent,
    promotionalConsent,
    isValidatingConsent,
    consentData,
    state.currentStep,
    state.contactMethod,
    actions,
  ]);

  return (
    <RegionContainer>
      <SectionContainer>
        <ListView id="new-project-fields">
          <ListViewItem
            key="new-project-department"
            onClick={() => {
              pushModal({
                type: "SELECT_DEPARTMENT",
                selected: state.opportunityInfo.hfb,
                onUpdate: (newHfbs) => {
                  actions.setDepartments(newHfbs);
                  actions.setTitle(
                    t("label.new_sales_project_title", {
                      firstname: state.customerInfo?.firstName,
                      lastname: state.customerInfo?.lastName,
                      hfb: translateHFBName(newHfbs[0]),
                    })
                  );
                },
                allowEmpty: false,
              });
              trackAnalytics("department");
            }}
            control="navigational"
            title={t("label.department")}
            leadingIcon={mapIcon}
            description={
              hasItems(state.opportunityInfo.hfb) ? (
                <Text>
                  {getSelectedDepartmentName(
                    departments,
                    state.opportunityInfo.hfb
                  ).join(", ")}
                </Text>
              ) : (
                <FormField
                  valid={hasItems(state.opportunityInfo.hfb)}
                  shouldValidate
                  validation={{
                    msg: t("label.required_field"),
                    id: "error",
                    type: "error",
                  }}
                />
              )
            }
          />

          <ListViewItem
            key="new-project-title"
            onClick={() => {
              actions.setStep("TITLE");
              trackAnalytics("project_title");
            }}
            control="navigational"
            title={t("label.project_title")}
            leadingIcon={briefcaseIcon}
            description={
              isEmpty(state.opportunityInfo.title) ? (
                <FormField
                  valid={!isEmpty(state.opportunityInfo.title)}
                  shouldValidate
                  validation={{
                    msg: t("label.required_field"),
                    id: "error",
                    type: "error",
                  }}
                />
              ) : (
                state.opportunityInfo.title
              )
            }
          />

          <CoworkerAndTeamSelectorListItem
            title={t("label.assigned_to")}
            selectionMode={{
              coworkerSelectionMode: "SINGLE",
              selectOnlyTeam: true,
            }}
            pushModal={pushModal}
            selected={
              teamSelection ??
              [state.assignedCoworker?.emailId].filter(isDefined)
            }
            setSelected={(selected) => {
              if (isTeamSelection(selected)) {
                actions.setAssignedTeam(selected.team.teamId);
              } else {
                actions.setAssignedCoworker(selected?.[0]);
              }
            }}
            searchContext="PROJECT"
          />

          <ListViewItem
            key="new-project-contact-method"
            control="navigational"
            title={t("label.contact_method")}
            leadingIcon={speechBubbleIcon}
            description={
              translateContactMethod(state.contactMethod) ?? (
                <FormField
                  valid={state.contactMethod !== undefined}
                  shouldValidate
                  validation={{
                    msg: t("label.required_field"),
                    id: "error",
                    type: "error",
                  }}
                />
              )
            }
            addOn={
              state.sendConfirmation ? t("prompt.sent_confirmation") : undefined
            }
            onClick={() => actions.setStep("CONTACT_METHOD")}
          />

          <ListViewItem
            title={t("label.customer_consent")}
            description={
              <>
                {consentDetails.text}

                <FormField
                  valid={isConsentValid(
                    state.customerConsents,
                    state.manualConsents,
                    consent_requirements?.disable_project_creation
                  )}
                  shouldValidate
                  validation={{
                    msg: consent_requirements?.disable_project_creation
                      ? t("prompt.require_consent_approval")
                      : t("label.required_field"),
                    id: "error",
                    type: "error",
                  }}
                />
              </>
            }
            leadingIcon={consentDetails.icon}
            control="navigational"
            onClick={() => {
              if (state.customerInfo) {
                pushModal({
                  type: "CONSENT",
                  customer: state.customerInfo,
                  departments: state.opportunityInfo.hfb,
                  manualConsents: state.manualConsents,
                  onSelectConsents: actions.setManualConsents,
                  pushModal,
                });
              }
            }}
          />
        </ListView>
      </SectionContainer>
    </RegionContainer>
  );
};
